import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Ruler, Target, Factory } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { towerTypes, IMG } from "../lib/data";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";

const TowerSolutions = () => {
  const { t, lang, isRTL } = useLanguage();
  const navigate = useNavigate();

  return (
    <div data-testid="tower-solutions-page">
      <PageHero label={t.towersPage.label} title={t.towersPage.heading} sub={t.towersPage.sub} image={IMG.towerSunset} />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20 md:space-y-28">
          {towerTypes.map((tower, i) => {
            const d = tower[lang];
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={tower.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
                data-testid={`tower-type-${tower.id}`}
              >
                <div className={`relative ${reverse ? "lg:order-2" : ""}`}>
                  <div className="absolute -inset-3 border border-[#D4AF37]/20 -z-10" />
                  <img src={tower.image} alt={d.title} className="w-full h-[340px] md:h-[420px] object-cover" />
                  <span className="absolute top-4 start-4 bg-[#0A1223]/80 backdrop-blur px-3 py-1.5 text-xs font-mono text-[#D4AF37] border border-white/12">
                    0{i + 1}
                  </span>
                </div>
                <div className={reverse ? "lg:order-1" : ""}>
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight">{d.title}</h2>
                  <p className="mt-4 text-base text-[#AAB6C6] leading-relaxed">{d.desc}</p>
                  <div className="mt-7 space-y-4">
                    <Spec icon={Ruler} label={t.common.typicalHeights} value={d.heights} />
                    <Spec icon={Target} label={t.common.applications} value={d.apps} />
                    <Spec icon={Factory} label={t.common.industriesServed} value={d.industries} />
                  </div>
                  <button
                    onClick={() => navigate("/request-quotation")}
                    data-testid={`tower-rfq-${tower.id}`}
                    className="btn-gold mt-8 inline-flex items-center gap-2 px-7 py-3.5 group"
                  >
                    {t.common.requestQuotation}
                    <ArrowRight size={18} className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
      <CTASection />
    </div>
  );
};

const Spec = ({ icon: I, label, value }) => (
  <div className="flex items-start gap-4 border-s-2 border-[#D4AF37]/40 ps-4">
    <I size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
    <div>
      <p className="section-label !text-slate-400 mb-1">{label}</p>
      <p className="text-sm text-slate-200">{value}</p>
    </div>
  </div>
);

export default TowerSolutions;
